// NETBANNER1: shared header notice, scoped review dialog over existing offer controls.
import { readNetworkOfferNotice, listNetworkOffers, listNetworkRecipeOffers, adoptNetworkOffer,
  declineNetworkOffer, answerNetworkRecipeOffer, listLiquorProducts, mapNetworkRecipeOffer,
  cloneNetworkRecipeOffer } from "./api.ts";
import { createNetworkOffers } from "./networkDiscountOffers.ts";
import { createNetworkRecipeOffers } from "./networkRecipeOffers.ts";
import type { Session } from "./session.ts";

interface Counts { recipes: number | null; discounts: number | null; locationId: string }
function node<K extends keyof HTMLElementTagNameMap>(tag: K, text?: string): HTMLElementTagNameMap[K] {
  const result = document.createElement(tag); if (text !== undefined) result.textContent = text; return result;
}
function isCounts(value: unknown): value is Counts {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return typeof v["locationId"] === "string" && [v["recipes"], v["discounts"]].every(n => n === null || (typeof n === "number" && Number.isSafeInteger(n) && n >= 0));
}
export function createNetworkOfferNotice(deps: {
  anchor: HTMLElement; current: () => Session | undefined; locationId: () => string | undefined;
  deviceId: () => string | undefined; active: () => boolean;
  toast: (message: string, variant: "success") => void;
}): { refresh: () => Promise<void>; reset: () => void } {
  const notice = node("aside"); notice.className = "purchase-approval-notice"; notice.hidden = true;
  notice.setAttribute("role", "status");
  const text = node("span"); const review = node("button", "Review offers");
  review.type = "button"; review.className = "btn-secondary btn-compact";
  notice.append(text, review); deps.anchor.after(notice);
  let counts: Counts | undefined, sequence = 0, dialog: HTMLDialogElement | undefined;
  let dialogScope: string | undefined, countsScope: string | undefined;
  function scope(): string | undefined {
    const s = deps.current();
    return s === undefined ? undefined : JSON.stringify([s.token, s.tenantId, s.apiBaseUrl, s.userId,
      deps.locationId(), deps.deviceId(), s.permissions["admin.location_settings.manage"], s.permissions["admin.menu_drink.manage"]]);
  }
  function close(): void { if (dialog?.open) dialog.close(); dialog?.remove(); dialog = undefined; dialogScope = undefined; }
  function reset(): void { sequence++; counts = undefined; countsScope = undefined; notice.hidden = true; close(); }
  async function refresh(): Promise<void> {
    const s = deps.current(), held = scope();
    if (s === undefined || held === undefined || !deps.active() ||
      (s.permissions["admin.location_settings.manage"] !== "allowed" && s.permissions["admin.menu_drink.manage"] !== "allowed")) {
      reset(); return;
    }
    if (countsScope !== undefined && countsScope !== held) { counts = undefined; countsScope = undefined; notice.hidden = true; close(); }
    if (dialogScope !== undefined && dialogScope !== held) close();
    const ticket = ++sequence;
    try {
      const result = await readNetworkOfferNotice(s.apiBaseUrl, s.tenantId, deps.locationId());
      if (ticket !== sequence || scope() !== held || !deps.active()) return;
      if (result.status === 401 || result.status === 403) { reset(); return; }
      if (result.status !== 200 || !isCounts(result.body)) throw new Error("notice unavailable");
      const previous = counts; counts = result.body; countsScope = held;
      if (dialog !== undefined && previous !== undefined &&
        ((previous.recipes !== null && counts.recipes === null) || (previous.discounts !== null && counts.discounts === null))) close();
      const labels: string[] = [];
      if ((counts.recipes ?? 0) > 0) labels.push(`${counts.recipes} shared ${counts.recipes === 1 ? "recipe" : "recipes"}`);
      if ((counts.discounts ?? 0) > 0) labels.push(`${counts.discounts} shared ${counts.discounts === 1 ? "discount" : "discounts"}`);
      text.textContent = labels.join(" · "); review.textContent = "Review offers";
      notice.hidden = labels.length === 0;
    } catch {
      if (ticket !== sequence || scope() !== held || !deps.active()) return;
      counts = undefined; countsScope = undefined; close(); text.textContent = "Shared offers unavailable"; review.textContent = "Retry"; notice.hidden = false;
    }
  }
  function open(): void {
    const s = deps.current(), held = scope(), c = counts;
    if (s === undefined || held === undefined || c === undefined || countsScope !== held || !deps.active()) { void refresh(); return; }
    close();
    const sheet = node("dialog"); sheet.className = "shared-offers-dialog"; dialog = sheet; dialogScope = held;
    const title = node("h2", "Shared recipes and discounts"); title.id = "shared-offers-heading";
    sheet.setAttribute("aria-labelledby", title.id);
    const done = node("button", "Close"); done.type = "button"; done.className = "btn-secondary";
    done.onclick = close; sheet.append(title, done); document.body.append(sheet);
    const current = (): boolean => dialog === sheet && sheet.open && scope() === held && deps.active();
    const answered = (): void => { window.dispatchEvent(new Event("network-offers-answered")); void refresh(); };
    function panel() {
      const section = node("section"), heading = node("h3"), list = node("div"), status = node("p");
      status.setAttribute("role", "status"); section.append(heading, list, status); sheet.append(section);
      return { section, title: heading, list, status };
    }
    function scopedOffers<T extends { status: number; body: unknown }>(result: T): T {
      if (result.status !== 200 || typeof result.body !== "object" || result.body === null) return result;
      const body = result.body as Record<string, unknown>;
      if (!Array.isArray(body["offers"])) return result;
      return { ...result, body: { ...body, offers: body["offers"].filter((offer: unknown) => {
        if (typeof offer !== "object" || offer === null) return false;
        const row = offer as Record<string, unknown>;
        if (row["source"] === "own") return false;
        return Array.isArray(row["locations"]) && (row["locations"].length === 0 || row["locations"].some((entry: unknown) =>
          typeof entry === "object" && entry !== null && (entry as Record<string, unknown>)["id"] === c!.locationId));
      }) } } as T;
    }
    if (c.discounts !== null) {
      const control = createNetworkOffers({ ...panel(), isCurrent: current, toast: deps.toast, onAdopted: () => {}, onAnswered: answered,
        fetchOffers: async () => scopedOffers(await listNetworkOffers(s.apiBaseUrl, s.tenantId)),
        answer: (id, kind) => kind === "adopt" ? adoptNetworkOffer(s.apiBaseUrl, s.tenantId, id) : declineNetworkOffer(s.apiBaseUrl, s.tenantId, id) });
      sheet.addEventListener("close", control.reset, { once: true });
      sheet.addEventListener("net-offers-open", () => { void control.refresh(); }, { once: true });
    }
    if (c.recipes !== null) {
      const control = createNetworkRecipeOffers({ ...panel(), source: "all", isCurrent: current, scope: () => current() ? held : null,
        toast: deps.toast, onMapped: answered, onAnswered: answered,
        fetchOffers: async () => scopedOffers(await listNetworkRecipeOffers(s.apiBaseUrl, s.tenantId)),
        answer: (id, kind) => answerNetworkRecipeOffer(s.apiBaseUrl, s.tenantId, id, kind),
        fetchItems: () => listLiquorProducts(s.apiBaseUrl, s.tenantId, { activeOnly: true }),
        map: (id, body) => mapNetworkRecipeOffer(s.apiBaseUrl, s.tenantId, id, body),
        clone: (id, body) => cloneNetworkRecipeOffer(s.apiBaseUrl, s.tenantId, id, body) });
      sheet.addEventListener("close", control.reset, { once: true });
      sheet.addEventListener("net-offers-open", () => { void control.refresh(); }, { once: true });
    }
    sheet.addEventListener("cancel", event => { event.preventDefault(); close(); });
    sheet.showModal(); sheet.dispatchEvent(new Event("net-offers-open")); done.focus();
  }
  review.onclick = open;
  window.addEventListener("focus", () => { if (deps.active()) void refresh(); });
  window.addEventListener("network-offers-answered", () => { if (deps.active()) void refresh(); });
  setInterval(() => { if (!document.hidden && deps.active()) void refresh(); }, 60_000);
  return { refresh, reset };
}
